'use client';

import React, { useEffect, useState } from 'react';

interface MockAnalyticsProps {
    isActive: boolean;
}

export function MockAnalytics({ isActive }: MockAnalyticsProps) {
    const [views, setViews] = useState(0);
    const [leads, setLeads] = useState(0);
    const [rate, setRate] = useState(0);

    useEffect(() => {
        if (isActive) {
            animateStats();
        } else {
            setViews(0);
            setLeads(0);
            setRate(0);
        }
    }, [isActive]);

    const animateStats = () => {
        // Animate numbers up
        let v = 0;
        const interval = setInterval(() => {
            v += Math.floor(Math.random() * 5) + 2;
            if (v > 124) {
                v = 124;
                clearInterval(interval);
            }
            setViews(v);
            setLeads(Math.floor(v * 0.12)); // 12% conv
            setRate(12.4);
        }, 50);
    };

    return (
        <div className="w-full h-full bg-[#FAFAFA] flex flex-col p-6 animate-in fade-in duration-700">

            {/* Breadcrumb / Top Bar */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        Waitlistly
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide border border-green-200">
                            Published
                        </span>
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">waitlistly-2.waitlistly.live</p>
                </div>
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                    <button className="bg-white border text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex-1 md:flex-none">Edit Design</button>
                    <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center justify-center gap-2 flex-1 md:flex-none shadow-sm shadow-indigo-200">
                        Visit Live <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
                <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2 font-medium text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Total Views
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{views}</div>
                    <div className="text-xs text-gray-400 mt-1">Unique page visits</div>
                </div>

                <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2 font-medium text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Total Leads
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{leads}</div>
                    <div className="text-xs text-gray-400 mt-1">Waitlist signups</div>
                </div>

                <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 text-indigo-600 mb-2 font-medium text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                        Conversion Rate
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{rate}%</div>
                    <div className="text-xs text-gray-400 mt-1">Signups per view</div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-col md:flex-row gap-6 flex-1">
                <div className="flex-1 bg-white rounded-xl border border-gray-100 p-4 md:p-6 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-900">Leads</h3>
                        <div className="flex gap-1">
                            <div className="w-16 md:w-24 h-6 md:h-8 bg-gray-50 rounded"></div>
                            <div className="w-16 md:w-24 h-6 md:h-8 bg-gray-50 rounded"></div>
                        </div>
                    </div>
                    {/* Fake Table */}
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => (
                            <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${isActive ? 'bg-indigo-50 animate-in slide-in-from-left duration-500' : 'opacity-0'}`} style={{ animationDelay: `${i * 200}ms` }}>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center text-xs font-bold text-indigo-700">U{i}</div>
                                    <div className="text-xs md:text-sm font-medium text-gray-900">user{i}@example.com</div>
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500">Just now</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="w-full md:w-80 space-y-6">
                    {/* Status Card */}
                    <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6 shadow-sm">
                        <h4 className="font-bold text-gray-900 text-sm mb-4 border-l-4 border-indigo-500 pl-2">Deployment Status</h4>
                        <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between text-sm font-medium mb-4">
                            Current State
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        </div>
                        <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Live URL</div>
                        <div className="text-indigo-600 text-sm truncate mb-4">waitlistly-2.waitlistly.live</div>
                        <button className="w-full border border-gray-200 py-2 rounded-lg text-xs font-bold text-gray-600 uppercase">Manage Deployment</button>
                    </div>
                </div>
            </div>


        </div>
    );
}
